<script lang="ts" setup>
import isNil from 'lodash/isNil'
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import i18n from '@/i18n'
import { useLeaguesStore } from '@/store/public/leagues'

interface LeagueBreadcrumbsItem {
  label: string
  path?: string
}

const leagues = useLeaguesStore()
const route = useRoute()

const items = computed<LeagueBreadcrumbsItem[]>((): LeagueBreadcrumbsItem[] => {
  const items = []

  items.push({
    label: i18n.global.t('leagues.labels.all-seasons'),
    path: '/leagues',
  })

  if (!isNil(leagues.selectedSeason)) {
    items.push({
      label: leagues.selectedSeason,
      path: `/leagues/${leagues.selectedSeason}`,
    })
  }

  if (!isNil(leagues.selectedSeason) && !isNil(leagues.selectedLeague)) {
    items.push({
      label: leagues.selectedLeague?.name,
      path: `/leagues/${leagues.selectedSeason}/${leagues.selectedLeague?.id}`,
    })
  }

  if (
    !isNil(leagues.selectedSeason) &&
    !isNil(leagues.selectedLeague) &&
    !isNil(leagues.selectedRound)
  ) {
    const section = isNil(leagues.selectedRound?.section_number_extra)
      ? String(leagues.selectedRound?.section_number)
      : `${leagues.selectedRound?.section_number} / ${leagues.selectedRound?.section_number_extra}`

    items.push({
      label: i18n.global.t('leagues.labels.section-round', {
        section,
        separator: '-',
        round: String(leagues.selectedRound?.number),
      }),
      path: `/leagues/${leagues.selectedSeason}/${leagues.selectedLeague?.id}/standings/${leagues.selectedRound?.id}`,
    })
  }

  if (
    !isNil(leagues.selectedSeason) &&
    !isNil(leagues.selectedLeague) &&
    !isNil(leagues.selectedRound) &&
    !isNil(leagues.selectedMatch)
  ) {
    items.push({
      label: `${leagues.selectedMatch?.standings?.home_team?.name} - ${leagues.selectedMatch?.standings?.away_team?.name}`,
      path: `/leagues/${leagues.selectedSeason}/${leagues.selectedLeague?.id}/standings/${leagues.selectedRound?.id}/${leagues.selectedMatch?.id}`,
    })
  }

  if (!isNil(route.meta.breadcrumbLabel)) {
    items.push({
      label: route.meta.breadcrumbLabel as string,
    })
  }

  return items
})
</script>

<template>
  <div v-if="items.length > 0" class="league-breadcrumbs">
    <div
      v-for="(item, i) in items"
      :key="`item-${i}`"
      class="league-breadcrumbs-item"
    >
      <RouterLink v-if="!isNil(item.path)" :to="{ path: item.path }">
        {{ item.label }}
      </RouterLink>

      <span v-else>
        {{ item.label }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'src/assets/scss/globals';

.league-breadcrumbs {
  display: flex;
  flex-flow: row wrap;
  padding: 0.25em 0.5em;
  background-color: $color-background-disabled;
  border-radius: 0.25em;
  font-size: 0.75em;

  .league-breadcrumbs-item:not(:last-child)::after {
    content: '»';
    margin: 0 0.5em;
    color: $color-text-disabled;
  }
}
</style>
